import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";
import { COMPANY } from "@/data/content";

// Datenschutzerklärung — GDPR-compliant privacy policy covering the site's
// actual data-processing surface: the contact form (stored in MongoDB +
// forwarded via Resend) and PostHog analytics (loaded from index.html).

const LAST_UPDATED = "15. January 2026";

const Section = ({ title, children, testid }) => (
  <div className="card-surface p-8 md:p-12 mb-8" data-testid={testid}>
    <h2 className="display-h3 mb-6">{title}</h2>
    <div className="space-y-4 text-bone-200/80 leading-relaxed">{children}</div>
  </div>
);

export default function Privacy() {
  return (
    <div className="relative">
      <section className="pt-24 md:pt-36 pb-16">
        <div className="container-x max-w-4xl">
          <Reveal>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 text-bone-200/60 hover:text-clay-400 transition-colors mb-12"
              data-testid="privacy-back"
            >
              <ArrowLeft size={14} />
              Zurück zur Startseite
            </Link>
          </Reveal>

          <Reveal delay={40}>
            <p className="eyebrow mb-6">Legal · DSGVO / GDPR</p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="display-h1">Datenschutzerklärung.</h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="body-lg mt-8 max-w-2xl">
              Diese Erklärung informiert Sie über Art, Umfang und Zweck der
              Erhebung und Verwendung personenbezogener Daten auf dieser
              Website. Stand: {LAST_UPDATED}.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-x max-w-4xl">
          <Reveal>
            <Section
              title="1. Verantwortlicher"
              testid="privacy-controller"
            >
              <p>
                Verantwortlich im Sinne der Datenschutz-Grundverordnung (DSGVO)
                ist:
              </p>
              <p className="text-bone-100">
                {COMPANY.legalName}
                <br />
                {COMPANY.address.line1}
                <br />
                {COMPANY.address.postal}, {COMPANY.address.country}
                <br />
                Telefon:{" "}
                <a
                  href={`tel:${COMPANY.phoneHref}`}
                  className="text-clay-400 hover:underline"
                >
                  {COMPANY.phone}
                </a>
                <br />
                E-Mail:{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-clay-400 hover:underline"
                >
                  {COMPANY.email}
                </a>
              </p>
              <p>
                Weitere Angaben zum Unternehmen finden Sie im{" "}
                <Link to="/impressum" className="text-clay-400 hover:underline">
                  Impressum
                </Link>
                .
              </p>
            </Section>
          </Reveal>

          <Reveal delay={40}>
            <Section title="2. Erhebung allgemeiner Zugriffsdaten">
              <p>
                Bei jedem Besuch dieser Website werden automatisch technische
                Zugriffsdaten übermittelt (z. B. IP-Adresse in gekürzter Form,
                Datum und Uhrzeit der Anfrage, aufgerufene Seite,
                Referrer-URL, verwendeter Browser und Betriebssystem). Diese
                Daten werden ausschließlich zur Sicherstellung des sicheren
                Betriebs der Website verarbeitet und nach kurzer Frist gelöscht.
              </p>
              <p>
                Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes
                Interesse an einem sicheren und funktionsfähigen Webangebot).
              </p>
            </Section>
          </Reveal>

          <Reveal delay={80}>
            <Section title="3. Kontaktformular" testid="privacy-contact-form">
              <p>
                Wenn Sie unser Kontaktformular nutzen, werden die dort
                angegebenen Daten (Name, E-Mail-Adresse, ggf. Firma,
                Telefonnummer, Interesse und Ihre Nachricht) an uns
                übermittelt, in unserer Datenbank gespeichert und per
                E-Mail an{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-clay-400 hover:underline"
                >
                  {COMPANY.email}
                </a>{" "}
                weitergeleitet.
              </p>
              <p>
                Für den E-Mail-Versand nutzen wir den Dienst{" "}
                <strong className="text-bone-100">Resend</strong> (Resend, Inc.,
                2261 Market Street, San Francisco, CA 94114, USA). Resend
                verarbeitet die eingegebenen Daten ausschließlich zum Zweck
                der Übermittlung der Nachricht an uns. Ein internationaler
                Datentransfer erfolgt auf Grundlage der EU-Standardvertragsklauseln.
              </p>
              <p>
                Rechtsgrundlage der Verarbeitung ist Art. 6 Abs. 1 lit. b
                DSGVO (Anbahnung eines Vertragsverhältnisses) bzw. Art. 6 Abs.
                1 lit. f DSGVO (berechtigtes Interesse an der Beantwortung von
                Anfragen). Die Daten werden gelöscht, sobald sie für den
                Zweck ihrer Verarbeitung nicht mehr erforderlich sind — in
                der Regel spätestens nach 24 Monaten, sofern keine
                gesetzlichen Aufbewahrungspflichten bestehen.
              </p>
            </Section>
          </Reveal>

          <Reveal delay={120}>
            <Section title="4. Analytics (PostHog)" testid="privacy-analytics">
              <p>
                Diese Website nutzt <strong className="text-bone-100">PostHog</strong>{" "}
                (PostHog Inc., 2261 Market Street #4008, San Francisco, CA
                94114, USA) zur pseudonymisierten Reichweitenmessung. PostHog
                verarbeitet dabei technische Nutzungsdaten wie
                IP-Adressen (gekürzt), Session-IDs und Interaktionen mit der
                Website.
              </p>
              <p>
                Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Sie können der
                Verarbeitung jederzeit widersprechen, indem Sie uns unter{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-clay-400 hover:underline"
                >
                  {COMPANY.email}
                </a>{" "}
                kontaktieren.
              </p>
            </Section>
          </Reveal>

          <Reveal delay={160}>
            <Section title="5. Ihre Rechte">
              <p>Sie haben nach der DSGVO folgende Rechte uns gegenüber:</p>
              <ul className="space-y-3 mt-4">
                {[
                  "Recht auf Auskunft (Art. 15 DSGVO)",
                  "Recht auf Berichtigung (Art. 16 DSGVO)",
                  "Recht auf Löschung (Art. 17 DSGVO)",
                  "Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)",
                  "Recht auf Datenübertragbarkeit (Art. 20 DSGVO)",
                  "Widerspruchsrecht (Art. 21 DSGVO)",
                  "Recht auf Beschwerde bei einer Aufsichtsbehörde (Art. 77 DSGVO)",
                ].map((r) => (
                  <li key={r} className="flex items-start gap-3">
                    <span className="text-clay-400 mt-1">—</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
              <p>
                Zur Ausübung Ihrer Rechte genügt eine formlose E-Mail an{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-clay-400 hover:underline"
                >
                  {COMPANY.email}
                </a>
                .
              </p>
              <p>
                Die zuständige Aufsichtsbehörde für uns ist die Berliner
                Beauftragte für Datenschutz und Informationsfreiheit,
                Alt-Moabit 59-61, 10555 Berlin.
              </p>
            </Section>
          </Reveal>

          <Reveal delay={200}>
            <Section title="6. Datensicherheit">
              <p>
                Wir setzen aktuelle technische und organisatorische Maßnahmen
                ein, um Ihre Daten gegen Manipulation, Verlust und
                unberechtigten Zugriff zu schützen. Der Datenverkehr auf
                dieser Website ist durchgängig TLS-verschlüsselt.
              </p>
            </Section>
          </Reveal>

          <Reveal delay={240}>
            <Section title="7. Änderungen dieser Datenschutzerklärung">
              <p>
                Wir behalten uns vor, diese Datenschutzerklärung anzupassen,
                sofern Änderungen der Rechtslage oder Änderungen der von uns
                angebotenen Dienste dies erfordern. Die jeweils aktuelle
                Fassung ist stets auf dieser Seite abrufbar.
              </p>
            </Section>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
